
import { MortgageData } from '@/types/calculator';
import { formatInterestRate } from '@/components/calculator/utils/formatters';

export const generateMortgageTextReport = (mortgageData: MortgageData): string => {
  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const monthlyPI = mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest;
  const totalInterestPaid = (monthlyPI * mortgageData.loanTerm * 12) - loanAmount;
  const loanToValue = mortgageData.homePrice > 0 ? (loanAmount / mortgageData.homePrice) * 100 : 0;
  const cashToClose = mortgageData.downPayment + mortgageData.closingCosts.total;

  return `
EquiForge™ Mortgage Calculator Report
====================================

PROPERTY SUMMARY
================
Home Price: ${formatCurrency(mortgageData.homePrice)}
Property Type: ${mortgageData.propertyType?.replace(/([A-Z])/g, ' $1').trim() || 'Single Family'}
Location: ${mortgageData.location?.city && mortgageData.location?.state 
  ? `${mortgageData.location.city}, ${mortgageData.location.state}`
  : mortgageData.location?.state || 'Not specified'}
Loan Type: ${mortgageData.loanType.toUpperCase()}

KEY FINANCIAL METRICS
=====================
Monthly Payment: ${formatCurrency(mortgageData.monthlyPayment.total)}
Down Payment: ${formatCurrency(mortgageData.downPayment)} (${mortgageData.downPaymentPercentage}%)
Closing Costs: ${formatCurrency(mortgageData.closingCosts.total)}
Cash to Close: ${formatCurrency(cashToClose)}

MONTHLY PAYMENT BREAKDOWN
=========================
Principal & Interest: ${formatCurrency(monthlyPI)}
Property Taxes: ${formatCurrency(mortgageData.monthlyPayment.taxes)}
Home Insurance: ${formatCurrency(mortgageData.monthlyPayment.insurance)}
${mortgageData.monthlyPayment.pmi > 0 ? `PMI/MIP: ${formatCurrency(mortgageData.monthlyPayment.pmi)}\n` : ''}${mortgageData.monthlyPayment.hoa > 0 ? `HOA Fees: ${formatCurrency(mortgageData.monthlyPayment.hoa)}\n` : ''}
Total Monthly Payment: ${formatCurrency(mortgageData.monthlyPayment.total)}

LOAN DETAILS
============
Loan Amount: ${formatCurrency(loanAmount)}
Interest Rate: ${formatInterestRate(mortgageData.interestRate)}
Loan Term: ${mortgageData.loanTerm} years
Loan-to-Value Ratio: ${loanToValue.toFixed(1)}%
Credit Score: ${mortgageData.creditScore}
Total Interest Paid: ${formatCurrency(totalInterestPaid)}
Total of Payments: ${formatCurrency(loanAmount + totalInterestPaid)}

CLOSING COSTS BREAKDOWN
=======================
Loan Origination Fee: ${formatCurrency(mortgageData.closingCosts.loanOrigination)}
Appraisal Fee: ${formatCurrency(mortgageData.closingCosts.appraisalFee)}
Title Services: ${formatCurrency(mortgageData.closingCosts.titleServices)}
Government Recording: ${formatCurrency(mortgageData.closingCosts.governmentRecordingCharges)}
Prepaids: ${formatCurrency(mortgageData.closingCosts.prepaids)}
Initial Escrow: ${formatCurrency(mortgageData.closingCosts.initialEscrow)}
Other Costs: ${formatCurrency(mortgageData.closingCosts.other)}
${mortgageData.closingCosts.fundingFee > 0 ? `Funding Fee: ${formatCurrency(mortgageData.closingCosts.fundingFee)}\n` : ''}${mortgageData.closingCosts.sellerCreditAmount > 0 ? `Seller Credit: -${formatCurrency(mortgageData.closingCosts.sellerCreditAmount)}\n` : ''}
Total Closing Costs: ${formatCurrency(mortgageData.closingCosts.total)}

=====================================
Report Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
Generated by EquiForge™ Platform

IMPORTANT DISCLAIMER:
These calculations are estimates only and are provided for informational purposes.
This information is not coming from a lender, bank, or any financial institution.
This is not a loan approval or commitment to lend.
Always consult with a qualified mortgage professional.
`.trim();
};

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error);
    return false;
  }
};

export const downloadTextFile = (text: string, filename: string): void => {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
};
